import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef } from "react";

import { deepEquals } from "@swa-ui/object";
import { classNames } from "@swa-ui/string";

import { AnimationContext } from "../AnimationProvider";
import { transformationActions } from "../defines/transformationActions";
import { Transition } from "../Transition";
import styles from "./Transform.module.scss";

const SCREEN_CAPTURE_AND_UNIT_TEST_STYLES = {
  DELAY: 0,
  DURATION: 0.001,
};

const REFLOW_ACTIONS = new Set(["height", "scaleX", "scaleY", "translateX", "translateY", "width"]);

export const Transform = React.forwardRef((props, ref) => {
  const {
    alwaysFireTransformationEnd = true,
    children,
    className,
    delay,
    duration,
    id,
    immediate,
    initial,
    onTransformationEnd,
    stackingContext,
    style,
    transformOrigin,
    transformations,
  } = props;
  const { enableReflowAnimations } = useContext(AnimationContext);
  const previousTransformations = useRef(transformations);

  useEffect(() => {
    if (alwaysFireTransformationEnd) {
      if (deepEquals(previousTransformations.current, transformations)) {
        onTransformationEnd?.();
      } else {
        previousTransformations.current = { ...transformations };
      }
    }
  });

  return (
    <Transition {...getProps()} ref={ref}>
      {children}
    </Transition>
  );

  function getProps() {
    return {
      animate: translateTransformations(transformations),
      "aria-hidden": props["aria-hidden"],
      className: getClass(),
      id,
      initial:
        isTestEnvironment() || shouldMakeTransformationImmediate()
          ? false
          : translateTransformations(initial),
      onTransformationEnd,
      style,
      transition: getTransition(),
    };
  }

  function getClass() {
    return classNames(className, {
      [styles[`origin-${transformOrigin}`]]: transformOrigin,
      [styles.stackingContext]: stackingContext,
    });
  }

  function translateTransformations(formattedOriginally) {
    const formattedNew = {};

    if (formattedOriginally) {
      formattedOriginally.forEach((transformation) => {
        const { action, amount } =
          typeof transformation !== "string"
            ? transformation
            : transformation === "none"
            ? {}
            : transformationActions[transformation];

        if (action && action !== "none") {
          formattedNew[action] = amount.indexOf("%") !== -1 ? amount : parseFloat(amount);
        }
      });
    }

    return formattedNew;
  }

  function getTransition() {
    const isTest = isTestEnvironment();

    return {
      delay: isTest || !delay ? 0 : delay / 1000,
      duration: isTest || immediate ? 0 : duration !== undefined ? duration / 1000 : undefined,
      ease: "linear",
    };
  }

  function shouldMakeTransformationImmediate() {
    return !enableReflowAnimations;
  }

  function isTestEnvironment() {
    return process.env.NODE_ENV === "screen-capture";
  }
});

export const transformPropTypes = {
  /**
   * When switching ReactSpring to Motion, it was realized that Motion, when no animation occurs,
   * does not fire onTransformationEnd events. This typically happens when the element is already
   * in the state that the animation would transition to. This is different from the
   * ReactSpring behavior, and therefore cause problems with the existing Transform implementation.
   * Setting this prop will ensure that onTransformationEnd will fire for each animation, even when
   * animation isn't needed. This will be on by default, but can be turned off as needed.
   */
  alwaysFireTransformationEnd: PropTypes.bool,

  /** Indicates if content is hidden. */
  "aria-hidden": PropTypes.bool,

  /** Content to be rendered. This is the content that Transform will act upon. */
  children: PropTypes.node,

  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Milliseconds to delay before animation begins. */
  delay: PropTypes.number,

  /**
   * By default, the duration of the animation will be determined by ReactSpring's physics algorithm, but can be set
   * to a milliseconds value, or zero for immediate movement. If zero is given, the onTransformationEnd event will not
   * be fired.
   */
  duration: PropTypes.number,

  /** Unique id given to Transform container */
  id: PropTypes.string,

  /** If immediate is true, no animation will occur and the transformation will be immediate. */
  immediate: PropTypes.bool,

  /** Function called when the animation is complete. Will not be called when zero duration is given. */
  onTransformationEnd: PropTypes.func,

  /**
   * How close to the end result the animated value gets before we consider it to be "there". ReactSpring default is
   * 0.01.
   */
  precision: PropTypes.number,

  /**
   * Option allows the caller to indicate if a new stacking order should be defined, which changes z-index's effect.
   */
  stackingContext: PropTypes.bool,

  /** style provides a way to add CSS values in addition to the properties that will be transformed. */
  style: PropTypes.object,

  /**
   * Array of transformations to be performed. The transform values will be combined by Transform and will animated.
   * It's important to note that no transformation will occur if the new values do not change from the existing
   * values. Also worth noting, height and width can be animated, but animating these values will cause the browser to
   * re-layout and repaint which is very non-performant and should be used judiciously.
   *
   * In addition to the actions listed below, shorthand values can be used such as "rotate90" as defined by
   * transformationActions.
   *
   * Avoid attempting to transform the same value more than once such as having more than one rotateZ actions. Only
   * the last action definition will be applied.
   */
  transformations: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.oneOf(Object.keys(transformationActions)),
      PropTypes.shape({
        action: PropTypes.oneOf([
          "height",
          "none",
          "opacity",
          "rotateX",
          "rotateY",
          "rotateZ",
          "scaleX",
          "scaleY",
          "scaleZ",
          "translateX",
          "translateY",
          "translateZ",
          "width",
        ]),
        amount: PropTypes.string,
      }),
    ])
  ),

  /**
   * transformOrigin pertains to how things are rotated. By default, elements are rotated around the center, but this
   * prop allow flexibility how items rotate.
   */
  transformOrigin: PropTypes.oneOf([
    "center",
    "center-bottom",
    "center-top",
    "left-bottom",
    "left-center",
    "left-top",
    "right-bottom",
    "right-center",
    "right-top",
  ]),
};

Transform.displayName = "Transform";

Transform.propTypes = transformPropTypes;
